import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { ClipboardList, Plus, Trash2, Send, Link2, CheckCircle2, XCircle, FileText, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  draft: { label: 'Entwurf', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  sent: { label: 'Gesendet', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  approved: { label: 'Genehmigt', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  declined: { label: 'Abgelehnt', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const DentalEstimatesPage = () => {
  const [customers, setCustomers] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [settings, setSettings] = useState({ dentotar_tpw: 1.0, tarif222_tpw: 1.0 });
  const [estimates, setEstimates] = useState([]);
  const [detail, setDetail] = useState(null);

  // Formular
  const [customerId, setCustomerId] = useState('');
  const [title, setTitle] = useState('Kostenvoranschlag');
  const [system, setSystem] = useState('DENTOTAR');
  const [selTariff, setSelTariff] = useState('');
  const [minutes, setMinutes] = useState('');
  const [qty, setQty] = useState('1');
  const [items, setItems] = useState([]);
  const [matDesc, setMatDesc] = useState('');
  const [matAmount, setMatAmount] = useState('');
  const [materials, setMaterials] = useState([]);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);

  const loadEstimates = useCallback(async () => {
    try { const r = await axios.get(`${API}/dental/billing/estimates`); setEstimates(r.data?.items || []); }
    catch { toast.error('Kostenvoranschläge konnten nicht geladen werden'); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.customers || []))).catch(() => {});
    axios.get(`${API}/dental/billing/tariffs`).then((r) => setTariffs(r.data?.items || [])).catch(() => {});
    axios.get(`${API}/dental/billing/settings`).then((r) => setSettings(r.data)).catch(() => {});
    loadEstimates();
  }, [loadEstimates]);

  const tariffsForSystem = useMemo(() => tariffs.filter((t) => t.system === system && t.active !== false), [tariffs, system]);
  const selected = useMemo(() => tariffs.find((t) => t.id === selTariff), [tariffs, selTariff]);
  const tpwFor = (sys) => (sys === 'TARIF222' ? settings.tarif222_tpw : settings.dentotar_tpw);

  const lineAmount = (it) => {
    const t = it.tariff; const unit = +(Number(t.tax_points || 0) * tpwFor(t.system)).toFixed(2);
    if (t.is_time_based) { const per = Math.max(1, Number(t.minutes_per_unit || 5)); return +(unit * Math.max(1, Math.ceil(Number(it.minutes || 0) / per))).toFixed(2); }
    return +(unit * Number(it.quantity || 1)).toFixed(2);
  };
  const total = +(items.reduce((s, it) => s + lineAmount(it), 0) + materials.reduce((s, m) => s + Number(m.amount || 0), 0)).toFixed(2);

  const addItem = () => {
    if (!selected) { toast.error('Bitte Position wählen'); return; }
    if (selected.is_time_based && !(Number(minutes) > 0)) { toast.error('Minuten erforderlich'); return; }
    setItems([...items, { tariff: selected, minutes: selected.is_time_based ? Number(minutes) : null, quantity: selected.is_time_based ? null : Number(qty || 1) }]);
    setSelTariff(''); setMinutes(''); setQty('1');
  };
  const addMaterial = () => {
    if (!matDesc.trim() || !(Number(matAmount) > 0)) { toast.error('Material + Betrag erforderlich'); return; }
    setMaterials([...materials, { description: matDesc.trim(), amount: Number(matAmount), tax_rate: 8.1 }]);
    setMatDesc(''); setMatAmount('');
  };

  const create = async () => {
    if (!customerId) { toast.error('Bitte Patient wählen'); return; }
    if (items.length === 0 && materials.length === 0) { toast.error('Mindestens eine Position'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/dental/billing/estimates`, {
        customer_id: customerId, title,
        items: items.map((it) => ({ tariff_id: it.tariff.id, minutes: it.minutes, quantity: it.quantity })),
        materials, note,
      });
      toast.success('Kostenvoranschlag erstellt');
      setCustomerId(''); setItems([]); setMaterials([]); setNote(''); setTitle('Kostenvoranschlag');
      loadEstimates();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erstellen fehlgeschlagen'); }
    finally { setCreating(false); }
  };

  const publicLink = (e) => `${window.location.origin}/kv/${e.public_token}${e.tenant_slug ? `?tenant=${e.tenant_slug}` : ''}`;
  const copyLink = async (e) => {
    try { await navigator.clipboard.writeText(publicLink(e)); toast.success('Freigabe-Link kopiert'); }
    catch { toast.message(publicLink(e)); }
  };
  const send = async (e) => { try { await axios.post(`${API}/dental/billing/estimates/${e.id}/send`); toast.success('Als gesendet markiert'); copyLink(e); loadEstimates(); } catch { toast.error('Fehlgeschlagen'); } };
  const setStatus = async (e, status) => { try { await axios.post(`${API}/dental/billing/estimates/${e.id}/status`, { status }); toast.success('Status aktualisiert'); loadEstimates(); } catch { toast.error('Fehlgeschlagen'); } };
  const toInvoice = async (e) => { try { const r = await axios.post(`${API}/dental/billing/estimates/${e.id}/invoice`); toast.success(`Rechnung ${r.data?.invoice_number} erstellt`); loadEstimates(); } catch (er) { toast.error(er?.response?.data?.detail || 'Fehlgeschlagen'); } };
  const del = async (e) => { if (!window.confirm('Kostenvoranschlag löschen?')) return; try { await axios.delete(`${API}/dental/billing/estimates/${e.id}`); toast.success('Gelöscht'); loadEstimates(); } catch { toast.error('Fehlgeschlagen'); } };

  return (
    <div className="space-y-6" data-testid="dental-estimates-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ClipboardList className="text-teal-600" size={24} /> Kostenvoranschläge</h1>
        <p className="text-sm text-slate-500">Behandlungen kalkulieren und vom Patienten digital per Link freigeben lassen.</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Neuer Kostenvoranschlag</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[240px]"><Label>Patient</Label><CustomerSearchSelect customers={customers} value={customerId} onValueChange={setCustomerId} placeholder="Patient suchen..." /></div>
            <div className="flex-1 min-w-[200px]"><Label>Titel</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="kv-title" /></div>
          </div>

          <div className="flex flex-wrap items-end gap-3 border-t pt-3">
            <div><Label>Tarif</Label>
              <Select value={system} onValueChange={(v) => { setSystem(v); setSelTariff(''); }}>
                <SelectTrigger className="w-48" data-testid="kv-system-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="DENTOTAR">DENTOTAR</SelectItem><SelectItem value="TARIF222">Tarif 222</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Position</Label>
              <Select value={selTariff} onValueChange={setSelTariff}>
                <SelectTrigger className="w-72" data-testid="kv-tariff-select"><SelectValue placeholder="Position wählen…" /></SelectTrigger>
                <SelectContent>
                  {tariffsForSystem.length === 0 && <div className="px-3 py-2 text-sm text-slate-400">Keine Tarife – unter „Dental Abrechnung" laden</div>}
                  {tariffsForSystem.map((t) => (<SelectItem key={t.id} value={t.id}>{t.code} · {t.description}{t.is_time_based ? ' ⏱' : ''}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {selected?.is_time_based
              ? <div><Label>Minuten</Label><Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} className="w-24" data-testid="kv-minutes" /></div>
              : <div><Label>Anzahl</Label><Input type="number" value={qty} onChange={(e) => setQty(e.target.value)} className="w-24" data-testid="kv-qty" /></div>}
            <Button onClick={addItem} data-testid="kv-add-item"><Plus size={16} className="mr-1" /> Position</Button>
          </div>

          {(items.length > 0 || materials.length > 0) && (
            <Table>
              <TableHeader><TableRow><TableHead>Position</TableHead><TableHead>Menge</TableHead><TableHead className="text-right">Betrag</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {items.map((it, i) => (
                  <TableRow key={i} data-testid={`kv-line-${i}`}>
                    <TableCell>{it.tariff.code} {it.tariff.description}</TableCell>
                    <TableCell>{it.tariff.is_time_based ? `${it.minutes} Min.` : `${it.quantity}×`}</TableCell>
                    <TableCell className="text-right">CHF {lineAmount(it).toFixed(2)}</TableCell>
                    <TableCell><Button size="icon" variant="ghost" onClick={() => setItems(items.filter((_, j) => j !== i))}><Trash2 size={14} /></Button></TableCell>
                  </TableRow>
                ))}
                {materials.map((m, i) => (
                  <TableRow key={`m${i}`}>
                    <TableCell>Material: {m.description}</TableCell>
                    <TableCell>1×</TableCell>
                    <TableCell className="text-right">CHF {Number(m.amount).toFixed(2)}</TableCell>
                    <TableCell><Button size="icon" variant="ghost" onClick={() => setMaterials(materials.filter((_, j) => j !== i))}><Trash2 size={14} /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]"><Label>Material (optional)</Label><Input value={matDesc} onChange={(e) => setMatDesc(e.target.value)} data-testid="kv-mat-desc" /></div>
            <div><Label>Betrag CHF</Label><Input type="number" step="0.05" value={matAmount} onChange={(e) => setMatAmount(e.target.value)} className="w-28" data-testid="kv-mat-amount" /></div>
            <Button variant="outline" onClick={addMaterial} data-testid="kv-add-mat"><Plus size={16} className="mr-1" /> Material</Button>
          </div>

          <div><Label>Bemerkung</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="kv-note" /></div>

          <div className="flex items-center justify-between">
            <div className="text-xl font-bold" data-testid="kv-total">Total: CHF {total.toFixed(2)}</div>
            <Button onClick={create} disabled={creating || !customerId} data-testid="kv-create"><FileText size={16} className="mr-2" /> Kostenvoranschlag erstellen</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Alle Kostenvoranschläge</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Nr.</TableHead><TableHead>Patient</TableHead><TableHead>Titel</TableHead><TableHead>Total</TableHead><TableHead>Status</TableHead><TableHead>Gültig bis</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow></TableHeader>
            <TableBody>
              {estimates.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">Noch keine Kostenvoranschläge</TableCell></TableRow>}
              {estimates.map((e) => {
                const b = STATUS[e.status] || STATUS.draft;
                return (
                  <TableRow key={e.id} data-testid={`kv-row-${e.kv_number}`}>
                    <TableCell className="font-mono text-xs">{e.kv_number}</TableCell>
                    <TableCell>{e.customer_name}</TableCell>
                    <TableCell>{e.title}</TableCell>
                    <TableCell>CHF {Number(e.total).toFixed(2)}</TableCell>
                    <TableCell><Badge variant="outline" className={b.cls}>{b.label}</Badge>{e.invoice_id && <span className="ml-1 text-[10px] text-emerald-600">verrechnet</span>}</TableCell>
                    <TableCell className="text-xs">{e.valid_until}</TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => setDetail(e)} title="Details" data-testid={`kv-view-${e.kv_number}`}><Eye size={14} /></Button>
                      {e.status === 'draft' && <Button size="sm" variant="outline" onClick={() => send(e)} data-testid={`kv-send-${e.kv_number}`}><Send size={14} className="mr-1" />Senden</Button>}
                      {(e.status === 'sent' || e.status === 'draft') && <Button size="icon" variant="ghost" onClick={() => copyLink(e)} title="Link kopieren" data-testid={`kv-link-${e.kv_number}`}><Link2 size={14} /></Button>}
                      {e.status === 'sent' && <Button size="icon" variant="ghost" className="text-emerald-600" onClick={() => setStatus(e, 'approved')} title="Genehmigen" data-testid={`kv-approve-${e.kv_number}`}><CheckCircle2 size={14} /></Button>}
                      {e.status === 'sent' && <Button size="icon" variant="ghost" className="text-red-600" onClick={() => setStatus(e, 'declined')} title="Ablehnen" data-testid={`kv-decline-${e.kv_number}`}><XCircle size={14} /></Button>}
                      {e.status === 'approved' && !e.invoice_id && <Button size="sm" variant="outline" onClick={() => toInvoice(e)} data-testid={`kv-invoice-${e.kv_number}`}><FileText size={14} className="mr-1" />In Rechnung</Button>}
                      <Button size="icon" variant="ghost" className="text-slate-400" onClick={() => del(e)} title="Löschen" data-testid={`kv-delete-${e.kv_number}`}><Trash2 size={14} /></Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="kv-detail-dialog">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.kv_number} · {detail.title}</DialogTitle></DialogHeader>
              <div className="space-y-2 text-sm">
                <div className="text-slate-500">Patient: <span className="text-slate-900">{detail.customer_name}</span></div>
                <Table>
                  <TableBody>
                    {(detail.line_items || []).map((li, i) => (
                      <TableRow key={i}><TableCell>{li.service_name || li.description}</TableCell><TableCell className="text-right">CHF {Number(li.total).toFixed(2)}</TableCell></TableRow>
                    ))}
                    <TableRow><TableCell className="font-bold">Total</TableCell><TableCell className="text-right font-bold">CHF {Number(detail.total).toFixed(2)}</TableCell></TableRow>
                  </TableBody>
                </Table>
                {detail.note && <div className="text-slate-600">Bemerkung: {detail.note}</div>}
                {detail.approval && (
                  <div className={`rounded px-3 py-2 ${detail.approval.approved ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                    {detail.approval.approved ? 'Genehmigt' : 'Abgelehnt'} von <strong>{detail.approval.name}</strong> am {(detail.approval.signed_at || '').slice(0, 10)}
                    {detail.approval.note && <div className="text-xs mt-1">„{detail.approval.note}"</div>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DentalEstimatesPage;
